import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getCoursesDescr } from 'utils';
import StaticData from 'components/StaticData';

// Helper function to get course description without course-level hours for history display
function getCourseDescrForHistory(course, courseLists) {
  if (!course) return '';

  const formatCheckCourses = (checkCourses) =>
    checkCourses
      .map((cc, index, array) => {
        const prefix = index > 0 && index === array.length - 1 ? 'and ' : '';
        return `${prefix}${cc.subject} ${cc.number}`;
      })
      .join(', ');

  const formatRecommendedCourses = (courses) =>
    courses
      ?.map((c, index) => {
        const courseDetails = [
          c.connector,
          c.subject,
          c.number,
          c.proposedTitle ? 'Proposed' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return index === 0 ? courseDetails : `or ${courseDetails}`;
      })
      .join(', ');

  return [
    course.connector &&
      `\n<span data-connector="${course.connector}">${course.connector}</span>\n`,
    course.itemType === 'req_check' ? 'Check:' : '',
    course.gpa ? `Minimum ${course.gpa}` : '',
    course.checkSubtype === 'asu_cum_gpa' ? 'ASU Cumulative GPA' : '',
    course.checkSubtype === 'major_gpa' ? 'Major GPA' : '',
    ['subject', 'courses'].includes(course.checkSubtype) ? 'GPA in' : '',
    course.checkSubtype === 'subject' ? 'all' : '',
    course.checkSubtype === 'courses' && course.checkCourses
      ? formatCheckCourses(course.checkCourses)
      : '',

    course.subject && `${course.subject} `,
    course.number && `${course.number}:`,
    course.topic?.description ||
      (course.proposedTitle && `${course.proposedTitle} Proposed`) ||
      course.description ||
      '',
    course.division
      ? (['Upper', 'Lower'].includes(course.division)
          ? `${course.division} Division`
          : `${course.division}-Level`) +
        (course.itemType !== 'req_course_list' ? ' ' : '')
      : '',
    course.itemType === 'req_elective' || course.itemType === 'req_subject'
      ? 'Elective'
      : '',
    course.itemType === 'req_gs' && course.gs
      ? `${StaticData.gsList.find((gs) => gs.value === course.gs)?.label} (${course.gs})`
      : '',
    course.group ? `${course.group}:` : '',
    course.text || '',
    course.customText || '',
    courseLists && courseLists[course.courseListId]
      ? `List: ${courseLists[course.courseListId]?.courseListName || 'Untitled course list'}`
      : '',
    course?.recommended?.courses?.length
      ? `Recommended: ${formatRecommendedCourses(course.recommended.courses)}`
      : '',
    course.checkSubtype === 'subject' ? 'courses' : '',
    // Note: Removed course.hours display for history context
  ]
    .filter(Boolean)
    .join(' ');
}

// Helper function to format hours display for changes (without parentheses)
function formatHoursForChanges(obj) {
  if (!obj.hours || obj.hours === 0) return '';

  if (obj.maxHours && obj.maxHours > 0) {
    if (obj.hours !== obj.maxHours) {
      // Display range when hours != maxHours
      return `${obj.hours}-${obj.maxHours}`;
    } else {
      // Display single value when hours == maxHours
      return `${obj.hours}`;
    }
  } else {
    // Display single value when no maxHours
    return `${obj.hours}`;
  }
}

// Helper function to format hours display
function formatHoursDisplay(obj) {
  if (!obj.hours || obj.hours === 0) return '';

  if (obj.maxHours && obj.maxHours > 0) {
    if (obj.hours !== obj.maxHours) {
      // Display range when hours != maxHours
      return ` (${obj.hours}-${obj.maxHours})`;
    } else {
      // Display single value when hours == maxHours
      return ` (${obj.hours})`;
    }
  } else {
    // Display single value when no maxHours
    return ` (${obj.hours})`;
  }
}

// Helper function to get proper labels for subsection fields
function getSubsectionFieldLabel(fieldKey) {
  const fieldLabels = {
    subsectionName: 'Subsection',
    hours: 'Hours',
    upperDivisionHours: 'Upper Division Hours',
    notes: 'Notes',
  };
  return fieldLabels[fieldKey] || fieldKey.replace(/^\w/, (c) => c.toUpperCase());
}

function HistoryDetails({
  historyRequirements,
  historySubsections,
  historyDeleted,
  historyCourseListRequirements,
  subsectionName,
  handleClose,
}) {
  const actionLabelMap = {
    'REQUIREMENT-MODIFY': 'Edited list item',
    'REQUIREMENT-DELETE': 'Deleted list item',
    'REQUIREMENT-ADDED': 'Added list item',
  };
  console.log('historyDeleted: ', historyDeleted);
  console.log('historyCourseListRequirements: ', historyCourseListRequirements);
  console.log('historySubsections: ', historySubsections);
  console.log('historyRequirements: ', historyRequirements);
  console.log('subsectionName: ', subsectionName);
  const courseLists = useSelector((state) => state.checksheet.courseLists);
  const reverseData = [
    ...(historyRequirements || []),
    ...(historySubsections || []),
  ].reverse();

  function compareObjects(obj1String, obj2String) {
    const obj1 = obj1String ? JSON.parse(obj1String) : {};
    const obj2 = obj2String ? JSON.parse(obj2String) : {};
    const differences = [];

    if (obj2.requirementId) {
      const courses1 = obj1.courses || [];
      const courses2 = obj2.courses || [];

      let coursesDescr1 = '';
      let coursesDescr2 = '';

      for (let i = 0; i < Math.max(courses1.length, courses2.length); i++) {
        const course1 = courses1[i];
        const course2 = courses2[i];
        if (course1 !== undefined) {
          coursesDescr1 += getCourseDescrForHistory(course1, courseLists);
        }
        if (course2 !== undefined) {
          coursesDescr2 += getCourseDescrForHistory(course2, courseLists);
        }
      }

      if (coursesDescr1 !== coursesDescr2) {
        let obj1Display = coursesDescr1 + formatHoursDisplay(obj1);
        let obj2Display = coursesDescr2 + formatHoursDisplay(obj2);

        differences.push({
          key: coursesDescr1 === '' ? 'new' : 'requirement',
          obj1: obj1Display,
          obj2: obj2Display,
        });
      }

      ['hours', 'grade', 'notes'].forEach((key) => {
        if (obj1[key] !== obj2[key] && coursesDescr1 !== '') {
          let obj1Value = obj1[key];
          let obj2Value = obj2[key];

          // Format hours to show ranges when applicable
          if (key === 'hours') {
            obj1Value = formatHoursForChanges(obj1);
            obj2Value = formatHoursForChanges(obj2);
          }

          differences.push({
            key,
            obj1: obj1Value,
            obj2: obj2Value,
          });
        }
      });

      // Check for maxHours changes separately since it affects the hours display
      if (obj1.maxHours !== obj2.maxHours && coursesDescr1 !== '') {
        const obj1HoursFormatted = formatHoursForChanges(obj1);
        const obj2HoursFormatted = formatHoursForChanges(obj2);

        // Only add if the formatted display is different and we haven't already added a hours change
        if (obj1HoursFormatted !== obj2HoursFormatted &&
            !differences.some(diff => diff.key === 'hours')) {
          differences.push({
            key: 'hours',
            obj1: obj1HoursFormatted,
            obj2: obj2HoursFormatted,
          });
        }
      }
    } else if (obj2.subsectionId) {
      // Handle subsection changes - check all subsection fields
      const fieldsToCompare = ['subsectionName', 'hours', 'upperDivisionHours', 'notes'];

      fieldsToCompare.forEach((field) => {
        const value1 = obj1[field] || '';
        const value2 = obj2[field] || '';

        if (value1 !== value2) {
          differences.push({
            key: (field === 'subsectionName' && value1 === '') ? 'new' : field,
            obj1: value1,
            obj2: value2,
          });
        }
      });
    }

    return differences;
  }

  const hasDeleted = historyDeleted?.length > 0;
  useEffect(() => {
    document.querySelectorAll('[data-connector]').forEach((el) => {
      const connector = el.getAttribute('data-connector');
      if (connector) {
        el.classList.add(connector); // Dynamically use the connector as className
      }
    });
  }, []);

  return (
    <div
      style={{
        width:
          historyCourseListRequirements?.length > 0
            ? '1000px'
            : hasDeleted
              ? '800px'
              : '400px',
      }}
    >
      {/* Close Button */}
      <button
        className="border border-gray-3 rounded-circle lh-1"
        style={{ padding: '7px 10px', float: 'right' }}
        onClick={handleClose}
        aria-label="Close"
      >
        <FontAwesomeIcon
          icon={faXmark}
          width="10px"
          height="10px"
          className="text-gray-7"
        />
      </button>

      {/* ✅ Course List Requirement History Table */}
      {historyCourseListRequirements?.length > 0 ? (
        <>
          <div className="fs-4 fw-bold">History</div>
          <table className="table table-striped table-responsive">
            <thead style={{ whiteSpace: 'nowrap' }}>
              <tr>
                <th>Changes</th>
                <th>List Item</th>
                {/* <th>Credit Hours</th> */}
                <th>Updated By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {historyCourseListRequirements.map((item, index) => {
                let beforeObject = {},
                  afterObject = {};
                let listItemDescr = '';

                try {
                  beforeObject =
                    typeof item.before === 'string'
                      ? JSON.parse(item.before)
                      : item.before;
                  afterObject =
                    typeof item.after === 'string'
                      ? JSON.parse(item.after)
                      : item.after;

                  console.log('itemAction: ', item.action);
                  const formattedDate = new Date(
                    item.updatedOn
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                  });

                  // const creditHours =
                  // afterObject?.maxHours && afterObject?.maxHours !== 0
                  //   ? `${afterObject.hours} - ${afterObject.maxHours}`
                  //   : afterObject?.hours;

                  if (item.action === 'REQUIREMENT-MODIFY') {
                    console.log('item: ', item);
                    const differences = compareObjects(
                      item.before,
                      item.after,
                      true
                    );

                    return (
                      <tr key={index}>
                        <td className="text-nowrap">
                          {actionLabelMap[item.action] || item.action}
                        </td>
                        <td>
                          <div key={index} className="history-item">
                            <div>
                              {differences.length > 0 ? (
                                differences.map((diff, diffIndex) => (
                                  <span key={diffIndex}>
                                    <div
                                      style={{ whiteSpace: 'pre-line' }}
                                      dangerouslySetInnerHTML={{
                                        __html: diff.obj1,
                                      }}
                                    />
                                    <div className="text-teal-background">
                                      Changed to:
                                    </div>

                                    <div
                                      style={{ whiteSpace: 'pre-line' }}
                                      dangerouslySetInnerHTML={{
                                        __html: diff.obj2,
                                      }}
                                    />
                                  </span>
                                ))
                              ) : (
                                <div>No differences</div>
                              )}
                            </div>
                            {/* <hr style={{ margin: '1rem' }} /> */}
                          </div>
                        </td>
                        {/* <td>{creditHours}</td> */}
                        <td>{item.updatedBy}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  } else if (item.action === 'REQUIREMENT-ADDED') {
                    listItemDescr = getCoursesDescr(
                      afterObject?.courses || [],
                      courseLists,
                      true
                    );

                    return (
                      <tr key={index}>
                        <td className="text-nowrap">
                          {actionLabelMap[item.action] || item.action}
                        </td>
                        <td
                          style={{ whiteSpace: 'pre-line' }}
                          dangerouslySetInnerHTML={{ __html: listItemDescr }}
                        />

                        {/* <td>{creditHours}</td> */}
                        <td>{item.updatedBy}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  } else if (item.action === 'REQUIREMENT-DELETE') {
                    listItemDescr = getCoursesDescr(
                      beforeObject?.courses || [],
                      courseLists,
                      true
                    );

                    return (
                      <tr key={index}>
                        <td className="text-nowrap">
                          {actionLabelMap[item.action] || item.action}
                        </td>
                        <td
                          style={{ whiteSpace: 'pre-line' }}
                          dangerouslySetInnerHTML={{ __html: listItemDescr }}
                        />
                        {/* <td>{creditHours}</td> */}
                        <td>{item.updatedBy}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  }
                } catch (error) {
                  console.error('Error processing item:', error);
                  listItemDescr = '(Error parsing data)';
                }
                return null;
              })}
            </tbody>
          </table>
        </>
      ) : hasDeleted ? (
        <>
          <strong>{subsectionName}</strong>
          <h3>
            <FontAwesomeIcon
              icon={faTrash}
              title="Deleted requirements"
              className="fa-trash-inactive pe-1"
              style={{ width: '25px' }}
            />
            Deleted requirements
          </h3>
          <table className="table table-striped table-responsive">
            <thead style={{ whiteSpace: 'nowrap' }}>
              <tr>
                <th>Requirements</th>
                <th>Credit Hours</th>
                <th>Deleted By</th>
                <th>Date and time</th>
              </tr>
            </thead>
            <tbody>
              {historyDeleted.map((item, index) => {
                let beforeObject = {};
                try {
                  beforeObject =
                    typeof item.before === 'string'
                      ? JSON.parse(item.before)
                      : item.before;
                } catch (error) {
                  console.error(
                    `Error parsing item.before at index ${index}:`,
                    error
                  );
                  return null;
                }

                return (
                  <tr key={index}>
                    <td
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{
                        __html: getCoursesDescr(
                          beforeObject.courses ?? [],
                          courseLists
                        ),
                      }}
                    />
                    <td>
                      {beforeObject.maxHours && beforeObject.maxHours !== 0
                        ? `${beforeObject.hours} - ${beforeObject.maxHours}`
                        : beforeObject.hours}
                    </td>
                    <td>{item.updatedBy}</td>
                    <td>
                      {new Date(item.updatedOn).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : reverseData.length > 0 ? (
        <>
          <h3>Changes</h3>
          {reverseData.map((value, index) => {
            const formattedDate = new Date(value.updatedOn).toLocaleString(
              'en-US',
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
              }
            );

            const differences = compareObjects(value.before, value.after);

            // Check if the oldest item (last in the displayed list) is a new requirement
            const oldestItemIndex = reverseData.length - 1;
            const oldestItem = reverseData[oldestItemIndex];
            const oldestDifferences = compareObjects(oldestItem.before, oldestItem.after);
            const oldestIsNew = oldestDifferences.some(diff => diff.key === 'new');

            return (
              <div key={index} className="history-item">
                <div>
                  {value.updatedBy} * {formattedDate}
                </div>
                <div>
                  {differences.length > 0 ? (
                    differences.map((diff, diffIndex) => (
                      <span key={diffIndex}>
                        {/* Only show the header if we're not showing before/after or if it's a new requirement */}
                        {(oldestIsNew || diff.key === 'new' || !diff.obj1 || index < oldestItemIndex) && (
                          <div>
                            <strong>
                              {diff.key === 'new' ?
                               // Check if this is a subsection creation
                               (value.action === 'SUBSECTION-ADDED') ? 'Subsection Created:' : 'Requirement Created:' :
                               // Check if this is a subsection change by looking at the action
                               (value.action === 'SUBSECTION-MODIFIED') ?
                               `${getSubsectionFieldLabel(diff.key)} Changed to:` :
                               `${diff.key.replace(/^\w/, (c) => c.toUpperCase())} Changed to:`}
                            </strong>
                          </div>
                        )}
                        {/* Only show before/after format for the oldest item when it's not new */}
                        {!oldestIsNew && diff.key !== 'new' && diff.obj1 && index === oldestItemIndex && (
                          <div
                            style={{ whiteSpace: 'pre-line' }}
                            dangerouslySetInnerHTML={{ __html: diff.obj1 }}
                          />
                        )}
                        {!oldestIsNew && diff.key !== 'new' && diff.obj1 && index === oldestItemIndex && (
                          <div className="text-teal-background">
                            {/* Check if this is a subsection change by looking at the action */}
                            {(value.action === 'SUBSECTION-MODIFIED') ?
                             `${getSubsectionFieldLabel(diff.key)} Changed to:` : 'Requirement Changed to:'}
                          </div>
                        )}
                        <div
                          style={{ whiteSpace: 'pre-line' }}
                          dangerouslySetInnerHTML={{ __html: diff.obj2 }}
                        />
                      </span>
                    ))
                  ) : (
                    <div>No differences</div>
                  )}
                </div>
                <hr style={{ margin: '1rem' }} />
              </div>
            );
          })}
        </>
      ) : (
        <p style={{ textAlign: 'center', marginTop: '2rem' }}>
          No history available.
        </p>
      )}
    </div>
  );
}

export default HistoryDetails;
